import Cookies from 'js-cookie';
import Place from "../lib/Place";

const COOKIE_NAME = 'appt';

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

function clearAppointmentCookieData() {
    try {
        Cookies.remove(COOKIE_NAME, { path: '/' });
    } catch (error) {
        // Fall through.
    }

    return null;
}

function getAppointmentCookieData() {
    let data;

    try {
        data = JSON.parse(window.atob(Cookies.get(COOKIE_NAME)));
    } catch (error) {
        data = null;
    }

    if (data === null) {
        return clearAppointmentCookieData();
    }

    const structure = {
        'appointment': 'object',
        'clinic': 'object',
        'therapist': 'object',
        'expires': 'string',
    };

    for (let i in structure) {
        if (typeof data[i] !== structure[i]) {
            return clearAppointmentCookieData();
        }
    }

    return data;
}

function setAppointmentCookieData(data) {
    if (data === null) {
        Cookies.remove(COOKIE_NAME, { path: '/' });
        return;
    }

    const expires = window.moment(data.expires);

    Cookies.set(COOKIE_NAME, window.btoa(JSON.stringify({
        ...data,
        expires: expires.format('YYYY-MM-DD HH:mm:ss'),
    })), {
        path: '/',
        expires: expires.toDate(),
    });
}

/*
|--------------------------------------------------------------------------
| State
|--------------------------------------------------------------------------
*/

const state = {
    appointmentData: getAppointmentCookieData(),
    clinicId: null,
    clinic: null,
    date: null,
    place: null,
    placeId: null,
    sort: null,
};

/*
|--------------------------------------------------------------------------
| Actions
|--------------------------------------------------------------------------
*/

const actions = {
    setRawAppointmentData({ commit }, data) {
        commit('setAppointmentData', data);
    },

    setAppointmentData({ commit }, data) {
        commit('setAppointmentData', data);

        setAppointmentCookieData(data);
    },

    setClinicId({ commit, dispatch }, clinic) {
        if (clinic === null) {
            commit('setClinicId', null);
            commit('setClinic', null);
        } else if (clinic instanceof Object) {
            commit('setClinicId', clinic.ClinicID);
            commit('setClinic', clinic);
        } else {
            commit('setClinicId', clinic);
            dispatch('getClinic', clinic);
        }
    },

    getClinic({ commit, rootState }, clinicId) {
        // If we fail to load a clinic for any reason, we'll attempt to autofill
        // the location and auto-submit the search form, dispatching an event so
        // we can notify the user.
        const placesCallback = () => {
            if (location.search) {
                const data = window.queryString.parse(location.search);

                if (data.location !== undefined && typeof data.location === 'string') {
                    Place.loadPlaceUsingAddress(data.location, [
                        'search.submit',
                        'clinic.not-found',
                    ]);
                }
            }

            window.Events.silence('google.load', placesCallback);
        };

        const failedCallback = () => {
            commit('setClinic', null);
            commit('setClinicId', null);

            // Google Libs might not be loaded yet.
            if (rootState['google'].googleLoaded) {
                placesCallback();
            } else {
                window.Events.listen('google.load', placesCallback);
            }
        };

        window.axios.get('v1/clinic/' + clinicId).then(response => {
            if (!response.data.data.ClinicID) {
                failedCallback();
            } else {
                commit('setClinic', response.data.data);

                window.Events.dispatch('clinic.load', response.data.data);
            }
        }).catch(failedCallback);
    },

    setClinic({ commit }, clinic) {
        commit('setClinic', clinic);
    },

    setDate({ commit }, date) {
        commit('setDate', date);
    },

    setPlace({ commit }, place) {
        commit('setPlace', place);
    },

    setPlaceId({ commit }, placeId) {
        commit('setPlaceId', placeId);
    },

    setSort({ commit }, sort) {
        commit('setSort', sort);
    },
};

/*
|--------------------------------------------------------------------------
| Mutations
|--------------------------------------------------------------------------
*/

const mutations = {
    setAppointmentData(state, data) {
        state.appointmentData = data;
    },

    setDate(state, date) {
        state.date = null;
        state.date = date;
    },

    setClinicId(state, clinicId) {
        state.clinicId = clinicId;
    },

    setClinic(state, clinic) {
        state.clinic = clinic;
    },

    setPlace(state, place) {
        if (place === null) {
            state.place = null;
        } else {
            place.geometry.location.lat = place.geometry.location.lat();
            place.geometry.location.lng = place.geometry.location.lng();

            state.place = place;
        }
    },

    setPlaceId(state, placeId) {
        state.placeId = placeId;
    },

    setSort(state, sort) {
        state.sort = sort;
    },
};

/*
|--------------------------------------------------------------------------
| Export
|--------------------------------------------------------------------------
*/

export const appointment = {
    namespaced: true,
    actions,
    mutations,
    state,
};
